import React, { useRef, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { EditorView } from '@codemirror/view';
import { StreamLanguage } from '@codemirror/language';
import { jinja2 } from '@codemirror/legacy-modes/mode/jinja2';
import { HiOutlineChatAlt2, HiOutlineEmojiHappy } from 'react-icons/hi';
import { Template } from '../../../../../../../types/communication';
import { SLACK_EMOJI_SHORTCUTS, emojiFor } from '../../../../../../../utils/slackMrkdwn';
import { variableToken } from '../../../../../../../utils/templateVariables';
import SectionCard from '../../../shared/SectionCard';
import ConstantPicker from '../../../shared/ConstantPicker';
import VariablePicker from '../../../shared/VariablePicker';

interface SlackContentEditorProps {
    template: Template;
    disabled: boolean;
    onChange: (content: string) => void;
}

type ToolbarAction = { label: string; title: string; wrap?: [string, string]; insert?: string; className?: string };

const ACTIONS: ToolbarAction[] = [
    { label: 'B', title: 'Negrito', wrap: ['*', '*'], className: 'font-bold' },
    { label: 'I', title: 'Itálico', wrap: ['_', '_'], className: 'italic' },
    { label: 'S', title: 'Tachado', wrap: ['~', '~'], className: 'line-through' },
    { label: '</>', title: 'Código', wrap: ['`', '`'], className: 'font-mono text-[11px]' },
    { label: '❝', title: 'Citação', wrap: ['> ', ''] },
    { label: '•', title: 'Item de lista', wrap: ['• ', ''] },
    { label: '🔗', title: 'Link', wrap: ['<https://|', '>'] },
];

const SNIPPETS: { label: string; text: string }[] = [
    { label: 'Se / senão', text: '{% if CONDICAO %}texto quando sim{% else %}texto quando não{% endif %}' },
    { label: 'Valor padrão', text: "{{ VARIAVEL or '—' }}" },
    { label: 'Repetir lista', text: '{% for item in LISTA %}\n• {{ item.nome }}\n{% endfor %}' },
];

const SlackContentEditor: React.FC<SlackContentEditorProps> = ({ template, disabled, onChange }) => {
    const viewRef = useRef<EditorView | null>(null);
    const [emojiOpen, setEmojiOpen] = useState(false);

    const replaceSelection = (build: (selected: string) => string) => {
        const view = viewRef.current;
        if (!view) {
            onChange(`${template.content}${build('')}`);
            return;
        }
        const { from, to } = view.state.selection.main;
        const selected = view.state.sliceDoc(from, to);
        const text = build(selected);
        view.dispatch({ changes: { from, to, insert: text }, selection: { anchor: from + text.length } });
        view.focus();
    };

    const toolbarButton = 'h-7 min-w-[28px] px-1.5 rounded-md text-sm text-gray-600 dark:text-gray-200 hover:bg-slate-100 dark:hover:bg-slate-700';

    return (
        <SectionCard
            title="Mensagem"
            description="Formatação do Slack: *negrito*, _itálico_, ~tachado~ e <link|texto>. A lógica usa {% if %} e {{ variável }}."
            icon={<HiOutlineChatAlt2 size={18} />}
        >
            {!disabled && (
                <div className="flex flex-wrap items-center gap-1 mb-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 px-1.5 py-1">
                    {ACTIONS.map((action) => (
                        <button
                            key={action.title}
                            type="button"
                            title={action.title}
                            onClick={() => replaceSelection((s) => `${action.wrap?.[0] ?? ''}${s || action.title.toLowerCase()}${action.wrap?.[1] ?? ''}`)}
                            className={`${toolbarButton} ${action.className ?? ''}`}
                        >
                            {action.label}
                        </button>
                    ))}
                    <span className="w-px h-5 bg-gray-200 dark:bg-slate-600 mx-1" />
                    <div className="relative">
                        <button type="button" title="Emoji" onClick={() => setEmojiOpen((o) => !o)} className={toolbarButton}>
                            <HiOutlineEmojiHappy size={16} />
                        </button>
                        {emojiOpen && (
                            <div className="absolute z-30 mt-1 grid grid-cols-6 gap-1 p-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl">
                                {SLACK_EMOJI_SHORTCUTS.map((code) => (
                                    <button
                                        key={code}
                                        type="button"
                                        title={`:${code}:`}
                                        onClick={() => {
                                            replaceSelection(() => `:${code}:`);
                                            setEmojiOpen(false);
                                        }}
                                        className="w-8 h-8 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-lg"
                                    >
                                        {emojiFor(code)}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {SNIPPETS.map((snippet) => (
                        <button
                            key={snippet.label}
                            type="button"
                            onClick={() => replaceSelection(() => snippet.text)}
                            className="h-7 px-2 rounded-md text-xs text-gray-600 dark:text-gray-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                            + {snippet.label}
                        </button>
                    ))}
                    <span className="flex-1" />
                    <VariablePicker variables={template.variables} onPick={(name) => replaceSelection(() => variableToken(name))} />
                    <ConstantPicker onPick={(name) => replaceSelection(() => variableToken(name))} />
                </div>
            )}
            <div className="rounded-lg border border-gray-300 dark:border-slate-600 overflow-hidden">
                <CodeMirror
                    value={template.content}
                    editable={!disabled}
                    onChange={onChange}
                    onCreateEditor={(view) => {
                        viewRef.current = view;
                    }}
                    height="380px"
                    theme="light"
                    basicSetup={{ lineNumbers: false, foldGutter: false, highlightActiveLine: false }}
                    extensions={[EditorView.lineWrapping, StreamLanguage.define(jinja2)]}
                />
            </div>
        </SectionCard>
    );
};

export default SlackContentEditor;
